import { Card, CardHeader, CardTitle, CardBody, Chip } from '@vnetwork/ui/primitives';
import { InfoGrid, type InfoItem } from '@/pages/account/components/InfoGrid';
import { formatDate, formatMoney } from '@/lib/format';
import type { ServiceDetail } from '@/api/services';

const STATUS_TONE: Record<string, 'success' | 'info' | 'warning' | 'danger' | 'neutral'> = {
  active: 'success',
  trialing: 'info',
  'waiting-confirm': 'info',
  'waiting-create-service': 'warning',
  'on-resubmit-information': 'warning',
  'pending-terminated': 'danger',
  rejected: 'danger',
  cancelled: 'neutral',
};

const muted = { color: 'var(--vn-fg-muted)' } as const;

export interface SslInfoCardProps {
  detail: ServiceDetail;
}

/**
 * SSL service summary card.
 *
 * Mirrors legacy `SSL.SSL / Information`: surfaces the SSL-specific fields
 * (domain/IP, register price, recurring price, sales, status, active cycle,
 * renew cycle, stopped date) as an InfoGrid inside a Card. The generic
 * identity/lifecycle/pricing cards in `ServiceInformationTab` cover the rest.
 */
export function SslInfoCard({ detail }: SslInfoCardProps) {
  const isActive = detail.service_status === 'active';
  const isCancelled = detail.service_status === 'cancelled';
  const currency = detail.service_currency || 'VND';

  const items: InfoItem[] = [
    {
      label: 'Domain / IP',
      value: detail.name || <span style={muted}>—</span>,
    },
    {
      label: 'Status',
      value: (
        <Chip
          appearance="subtle"
          tone={STATUS_TONE[detail.service_status] ?? 'neutral'}
          size="sm"
        >
          {detail.service_status.replace(/-/g, ' ')}
        </Chip>
      ),
    },
    {
      label: 'Register price',
      value: formatMoney(detail.service_price_register, currency),
    },
    {
      label: 'Recurring price',
      value: formatMoney(detail.service_price_recurring, currency),
    },
    {
      label: 'Sales',
      value: detail.employee_names?.filter(Boolean).join(', ') || <span style={muted}>—</span>,
    },
    {
      label: 'Start date',
      value: formatDate(detail.service_start_date),
    },
    {
      label: 'Active cycle',
      value:
        detail.current_startdate || detail.current_enddate
          ? `${formatDate(detail.current_startdate)} → ${formatDate(detail.current_enddate)}`
          : <span style={muted}>—</span>,
    },
  ];

  if (isActive && (detail.continue_startdate || detail.continue_enddate)) {
    items.push({
      label: 'Renew cycle',
      value: `${formatDate(detail.continue_startdate)} → ${formatDate(detail.continue_enddate)}`,
    });
  }

  if (isCancelled && detail.service_stopped_date) {
    items.push({
      label: 'Stopped date',
      value: formatDate(detail.service_stopped_date),
    });
  }

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>SSL certificate</CardTitle>
      </CardHeader>
      <CardBody>
        <InfoGrid items={items} />
      </CardBody>
    </Card>
  );
}
